import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { destinations } from '../data';
import DestinationCard from '../components/DestinationCard';
import { motion } from 'framer-motion';
import { CheckCircle2, ShieldCheck, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import { useLanguage } from '../components/LanguageContext';
import { buildBreadcrumbSchema, buildOrganizationSchema } from '../lib/schema';

const DestinationsPage = () => {
  const { language, t, translateObject, getLocalizedPath } = useLanguage();
  const translatedDestinations = destinations.map(d => translateObject(d));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const characteristics = language === 'da' ? [
    'Adgangsforhold på hotellet, herunder værelser, badeværelser og fællesområder',
    'Muligheder for transfer mellem lufthavn og hotel',
    'Tilgængelighed på strande, herunder ramper og badesæder',
    'Nærområdets fremkommelighed, herunder fortove, restauranter og seværdigheder'
  ] : [
    'Access conditions at the hotel, including rooms, bathrooms, and common areas',
    'Transfer options between airport and hotel',
    'Accessibility at beaches, including ramps and beach wheelchairs',
    'Accessibility of the local area, including sidewalks, restaurants, and sights'
  ];

  const breadcrumbs = [
    {
      name: language === 'da' ? 'Forside' : 'Home',
      url: getLocalizedPath('home', language)
    },
    {
      name: language === 'da' ? 'Handicapvenlige feriedestinationer' : 'Accessible holiday destinations',
      url: getLocalizedPath('destinations', language)
    }
  ];
  const breadcrumbSchema = buildBreadcrumbSchema(breadcrumbs);
  const organizationSchema = buildOrganizationSchema({ language });

  return (
    <div className="pt-32 pb-24 min-h-screen relative">
      <SEO 
        title={language === 'da' ? "Handicapvenlig ferie i udlandet | Rejs trygt med Alora Rejser" : "Accessible Holiday Abroad | Travel Safely with Alora Rejser"} 
        description={language === 'da' ? "Leder du efter en handicapvenlig ferie i udlandet? Find tilgængelige hoteller og rejsemål med fokus på tryghed. Kontakt Alora Rejser i dag." : "Looking for an accessible holiday abroad? Find wheelchair friendly hotels and destinations with focus on safety. Contact Alora Rejser today."}
        ogImage="https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/ceef4750-2ca2-43c1-b82b-e06ba062b589.jpeg"
        schemaMarkup={[organizationSchema, breadcrumbSchema]}
      />
      {/* Weak background image overlay */}
      <div 
        className="absolute inset-0 z-0 opacity-15 pointer-events-none fixed"
        style={{
          backgroundImage: 'url("https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/378a20d8-af7c-4ed6-9273-84c31896612e.jpg")',
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      ></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <header className="mb-16 text-center max-w-3xl mx-auto">
          <motion.span 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-[#99A18C] font-bold uppercase tracking-widest text-xs mb-4 block"
          >
            {language === 'da' ? 'Håndplukkede Rejsemål' : 'Handpicked Destinations'}
          </motion.span>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-6xl font-serif font-bold text-[#99A18C] mb-6 italic"
          >
            {language === 'da' ? 'Find din næste handicapvenlige ferie' : 'Find Your Next Accessible Holiday'}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-stone-600 text-lg leading-relaxed"
          >
            {language === 'da' ? (
              <>
                <span className="block">
                  Drømmer du om en handicapvenlig ferie i udlandet med fokus på tryghed, komfort og god tilgængelighed? Hos <Link to={getLocalizedPath('home', language)} className="text-[#99A18C] underline font-semibold">Alora Rejser</Link> finder du nøje udvalgte destinationer med <Link to={getLocalizedPath('hotels', language)} className="text-[#99A18C] underline font-semibold">handicapvenlige hoteller</Link> og kørestolsvenlige omgivelser.
                </span>
                <span className="block mt-4">
                  Vi vælger rejsemål med gode adgangsforhold, tilgængelige strande og faciliteter som poollift og roll-in bruser, så du kan fokusere på oplevelserne frem for bekymringerne.
                </span>
              </>
            ) : (
              <>
                We have personally visited or thoroughly quality-assured all our destinations and <Link to={getLocalizedPath('hotels', language)} className="text-[#99A18C] underline font-semibold">wheelchair friendly hotels</Link>. Our goal is to create safe frameworks so you can enjoy your holiday without worries about accessibility.
              </>
            )}
          </motion.p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {translatedDestinations.map((dest, index) => (
            <DestinationCard key={dest.id} destination={dest} index={index} />
          ))}
        </div>

        {/* Pricing & Safety Banner */}
        <div className="bg-[#99A18C]/10 border border-[#99A18C]/20 rounded-3xl p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xs">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#99A18C]/20 flex items-center justify-center text-[#99A18C] shrink-0">
              <ShieldCheck size={26} />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-serif font-bold text-stone-800">
                {language === 'da' ? 'Gennemsigtige priser & Aloras Tryghedspakke' : 'Transparent Prices & Alora Safety Package'}
              </h3>
              <p className="text-xs sm:text-sm text-stone-600 mt-1">
                {language === 'da'
                  ? 'Vi oplyser gennemskuelige dagspriser baseret på dine behov. Se hvad der altid er inkluderet i din ferie.'
                  : 'We provide transparent daily prices based on your exact needs. See what is always included.'
                }
              </p>
            </div>
          </div>
          <Link
            to={getLocalizedPath('prices', language)}
            className="inline-flex items-center gap-2 whitespace-nowrap bg-white hover:bg-stone-50 text-[#99A18C] font-bold text-xs sm:text-sm px-5 py-3 rounded-xl border border-[#99A18C]/30 shadow-xs transition-colors shrink-0"
          >
            <span>{language === 'da' ? 'Læs om vores priser' : 'Read about our prices'}</span>
            <ArrowRight size={15} />
          </Link>
        </div>

        {/* Informational Section */}
        <div className="mt-24 max-w-4xl mx-auto bg-white rounded-[2.5rem] p-8 md:p-12 border border-[#99A18C]/10 shadow-sm">
          {language === 'da' ? (
            <div className="space-y-8 text-stone-600 leading-relaxed">
              <div>
                <h2 className="text-2xl md:text-3xl font-serif font-bold text-[#99A18C] mb-4 italic">
                  Handicapvenlig ferie i udlandet med fokus på tryghed og tilgængelighed
                </h2>
                <p className="mb-4">
                  En handicapvenlig ferie handler om meget mere end en rampe ved hotellets indgang. Det handler om at vide, hvad du møder på destinationen, før du rejser hjemmefra.
                </p>
                <p>
                  Derfor hjælper vi med at afklare adgangsforhold, transport, afstande, hotellets faciliteter og andre praktiske forhold, som kan have betydning for din ferieoplevelse.
                </p>
              </div>

              <div className="bg-stone-50 p-6 md:p-8 rounded-2xl border border-[#99A18C]/20">
                <h2 className="text-xl md:text-2xl font-serif font-bold text-[#99A18C] mb-4 italic">
                  Hvad kendetegner en handicapvenlig ferie?
                </h2>
                <p className="mb-4">
                  Når vi vurderer handicapvenlige rejser, ser vi blandt andet på:
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[#99A18C] mt-1 shrink-0" />
                    <span>Adgangsforhold på hotellet, herunder værelser, badeværelser og fællesområder</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[#99A18C] mt-1 shrink-0" />
                    <span>Muligheder for transfer mellem lufthavn og hotel</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[#99A18C] mt-1 shrink-0" />
                    <span>Lokale transportmuligheder på destinationen</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[#99A18C] mt-1 shrink-0" />
                    <span>Tilgængelighed ved poolområder, restauranter og øvrige faciliteter</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[#99A18C] mt-1 shrink-0" />
                    <span>Rejsendes individuelle behov i forhold til kørestol eller ganghjælpemidler</span>
                  </li>
                </ul>
                <p className="mb-4">
                  En handicapvenlig ferie handler om mere end enkelte faciliteter. Det handler om, at hele rejsen fungerer i praksis – fra ankomst til hjemrejse.
                </p>
                <p>
                  Derfor tager vi altid udgangspunkt i den enkelte rejsendes behov, uanset om du rejser med manuel kørestol, el-kørestol eller andre hjælpemidler. Målet er at finde en ferie, hvor tilgængelighed, komfort og oplevelser går hånd i hånd.
                </p>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-serif font-bold text-[#99A18C] mb-4 italic">
                  Handicapvenlige destinationer i Europa
                </h2>
                <p className="mb-6">
                  Mallorca, Kreta, Paris, Venedig og Østrig er blandt de destinationer, hvor vi hjælper danske rejsende med at finde handicapvenlige ferieoplevelser. Hver destination tilbyder forskellige oplevelser og niveauer af tilgængelighed, og vi hjælper med at finde det rejsemål, der passer bedst til dine ønsker.
                </p>
              </div>

              <div>
                <h3 className="text-lg md:text-xl font-serif font-bold text-[#99A18C] mb-3 italic">
                  Handicapvenlig ferie på Mallorca og Kreta
                </h3>
                <p className="mb-4">
                  Mallorca og Kreta er populære destinationer, hvis du drømmer om sol, varme og en handicapvenlig ferie. Begge øer byder på gode ferieoplevelser, og vi hjælper med at finde hoteller og områder, hvor adgangsforhold og faciliteter passer til dine behov.
                </p>
                <p className="mb-4">
                  På <strong>Mallorca</strong> kan du kombinere afslappende strandferie med hyggelige feriebyer og oplevelser på øen, mens <strong>Kreta</strong> byder på både smukke strande, græsk atmosfære og spændende udflugtsmuligheder.
                </p>
                <p className="mb-6">
                  Vi tilbyder også handicapvenlige rejser til <strong>Paris, Venedig og Østrig</strong>, hvis du foretrækker storbyoplevelser, kultur eller naturskønne omgivelser. Fælles for rejsemålene er, at vi hjælper dig med at finde en ferie, hvor de praktiske forhold er afklaret inden afrejse.
                </p>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-serif font-bold text-[#99A18C] mb-4 italic">
                  Få hjælp til at finde den rette handicapvenlige ferie i udlandet
                </h2>
                <p className="mb-4">
                  En handicapvenlig ferie i udlandet skal ikke føles som et kompromis. Den skal give mulighed for at opleve verden på en måde, hvor tryghed, komfort og feriestemning går hånd i hånd.
                </p>
                <p className="mb-4">
                  Kontakt Alora Rejser, hvis du ønsker personlig rådgivning om handicapvenlige rejser, tilgængelige hoteller og rejsemål i Europa.
                </p>
                <div className="space-y-1 mb-4 font-medium text-stone-700">
                  <p>
                    <strong>Telefon:</strong>{' '}
                    <a href="tel:+4540500780" className="text-[#99A18C] hover:underline">
                      +45 40 50 07 80
                    </a>
                  </p>
                  <p>
                    <strong>E-mail:</strong>{' '}
                    <a href="mailto:info@alorarejser.dk" className="text-[#99A18C] hover:underline">
                      info@alorarejser.dk
                    </a>
                  </p>
                </div>
                <p className="mb-4">
                  Vi glæder os til at hjælpe dig med at finde den ferie, der passer bedst til dine behov og ønsker.
                </p>
                <div className="mt-6">
                  <Link
                    to={getLocalizedPath('contact', language)}
                    className="inline-flex items-center justify-center bg-[#99A18C] text-white px-8 py-3.5 rounded-full font-bold hover:bg-[#8a917e] transition-all transform hover:-translate-y-0.5 shadow-md"
                  >
                    Kontakt os her
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-8 text-stone-600 leading-relaxed">
              <div>
                <h2 className="text-2xl md:text-3xl font-serif font-bold text-[#99A18C] mb-4 italic">
                  Accessible Holidays Abroad with Focus on Safety and Accessibility
                </h2>
                <p className="mb-4">
                  An accessible holiday is about much more than a ramp at the hotel entrance. It is about knowing what you will meet at the destination before leaving home.
                </p>
                <p>
                  Therefore, we help clarify access conditions, transport, distances, hotel facilities, and other practical aspects that can impact your holiday experience.
                </p>
              </div>

              <div className="bg-stone-50 p-6 md:p-8 rounded-2xl border border-[#99A18C]/20">
                <h2 className="text-xl md:text-2xl font-serif font-bold text-[#99A18C] mb-4 italic">
                  What Characterises an Accessible Holiday?
                </h2>
                <p className="mb-4">
                  When we evaluate accessible holidays, we look at among other things:
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[#99A18C] mt-1 shrink-0" />
                    <span>Access conditions at the hotel, including rooms, bathrooms, and common areas</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[#99A18C] mt-1 shrink-0" />
                    <span>Airport to hotel transfer options</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[#99A18C] mt-1 shrink-0" />
                    <span>Local transport options at the destination</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[#99A18C] mt-1 shrink-0" />
                    <span>Accessibility at pool areas, restaurants, and other facilities</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[#99A18C] mt-1 shrink-0" />
                    <span>The traveler's individual needs regarding wheelchair or walking aids</span>
                  </li>
                </ul>
                <p className="mb-4">
                  An accessible holiday is about more than individual facilities. It is about the entire journey working in practice – from arrival to return.
                </p>
                <p>
                  Therefore, we always base our approach on the individual traveler's needs, whether you travel with a manual wheelchair, power wheelchair, or other mobility aids. The goal is to find a holiday where accessibility, comfort, and experiences go hand in hand.
                </p>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-serif font-bold text-[#99A18C] mb-4 italic">
                  Accessible Destinations in Europe
                </h2>
                <p className="mb-6">
                  Mallorca, Crete, Paris, Venice, and Austria are among the destinations where we help travelers find accessible holiday experiences. Each destination offers different experiences and levels of accessibility, and we help find the destination that best suits your wishes.
                </p>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-serif font-bold text-[#99A18C] mb-4 italic">
                  Accessible Holidays in Mallorca and Crete
                </h2>
                <p className="mb-4">
                  Mallorca and Crete are popular destinations if you dream of sun, warmth, and an accessible holiday. Both islands offer great holiday experiences, and we help find hotels and areas where accessibility and facilities match your needs.
                </p>
                <p className="mb-4">
                  In <strong>Mallorca</strong>, you can combine a relaxing beach holiday with charming resort towns and island experiences, while <strong>Crete</strong> offers beautiful beaches, Greek atmosphere, and exciting excursion options.
                </p>
                <p className="mb-6">
                  We also offer accessible holidays to <strong>Paris, Venice, and Austria</strong>, if you prefer city experiences, culture, or scenic surroundings. Common to all destinations is that we help you find a holiday where practical conditions are settled before departure.
                </p>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-serif font-bold text-[#99A18C] mb-4 italic">
                  Get Help Finding the Right Accessible Holiday Abroad
                </h2>
                <p className="mb-4">
                  An accessible holiday abroad should not feel like a compromise. It should provide the opportunity to experience the world in a way where safety, comfort, and holiday spirit go hand in hand.
                </p>
                <p className="mb-4">
                  Contact Alora Rejser if you would like personal advice on accessible travel, wheelchair friendly hotels, and destinations in Europe.
                </p>
                <div className="space-y-1 mb-4 font-medium text-stone-700">
                  <p>
                    <strong>Phone:</strong>{' '}
                    <a href="tel:+4540500780" className="text-[#99A18C] hover:underline">
                      +45 40 50 07 80
                    </a>
                  </p>
                  <p>
                    <strong>Email:</strong>{' '}
                    <a href="mailto:info@alorarejser.dk" className="text-[#99A18C] hover:underline">
                      info@alorarejser.dk
                    </a>
                  </p>
                </div>
                <p className="mb-4">
                  We look forward to helping you find the holiday that best matches your needs and wishes.
                </p>
                <div className="mt-6">
                  <Link
                    to={getLocalizedPath('contact', language)}
                    className="inline-flex items-center justify-center bg-[#99A18C] text-white px-8 py-3.5 rounded-full font-bold hover:bg-[#8a917e] transition-all transform hover:-translate-y-0.5 shadow-md"
                  >
                    Contact us here
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DestinationsPage;
